// File: src/pages/tiles/incomm_void_report/Dashboard.js
//
// Incomm VOID Report. Pulls voided-label rows from ShipStore for a ship-date
// range (via our own API — the upstream sends no CORS headers), and lets the
// user filter, sort and export them as a CSV whose columns, order, headings
// and formatting they control.
//
// Every customisable part of the page — visible columns, sort, page size,
// date range, filters, and the whole export configuration — is stored per user
// via /api/user-preferences and restored on the next visit, from any browser.

import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import {
  Download, RefreshCw, AlertTriangle, Columns, Check, Copy, Clock, Info,
  Cloud, CloudOff, RotateCcw,
} from 'lucide-react';

import { fetchVoidReport } from './api';
import { COLUMNS, COLUMN_BY_ID } from './columns';
import { buildCsv, buildFilename, downloadCsv } from './csv';
import {
  readMirror, fetchPreferences, putPreferences, deletePreferences,
  defaultPrefs, resolveRange, rangeToPref,
} from './preferences';
import DateRangePicker from './components/DateRangePicker';
import SummaryCards from './components/SummaryCards';
import FilterBar from './components/FilterBar';
import VoidTable from './components/VoidTable';
import ExportModal from './components/ExportModal';
import QuickExportSummary from './components/QuickExportSummary';

const EMPTY_FILTERS = { status: 'all', voidedBy: 'all', carrier: 'all', reshipped: 'all' };

// How long to sit on a change before writing it. Long enough that dragging a
// column or stepping through page sizes is one write, short enough that a user
// who changes something and closes the tab still has it saved.
const SAVE_DEBOUNCE_MS = 700;

const IncommVoidReport = () => {
  // Rendered from the local mirror so the page does not flash defaults while
  // the real settings are in flight; the server's copy overwrites it on load.
  const initial = useRef(readMirror()).current;

  const [range, setRange] = useState(() => resolveRange(initial.range));
  const [visibleColumns, setVisibleColumns] = useState(initial.tableColumns);
  const [sort, setSort] = useState(initial.sort);
  const [pageSize, setPageSize] = useState(initial.pageSize);
  const [exportScope, setExportScope] = useState(initial.exportScope);
  const [exportPreset, setExportPreset] = useState(initial.export);
  // Search is intentionally not persisted: it is a transient "find this order"
  // lookup, and restoring it would make the tile look empty on open.
  const [filters, setFilters] = useState({ search: '', ...initial.filters });

  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);

  const [showColumnMenu, setShowColumnMenu] = useState(false);
  const [showExport, setShowExport] = useState(false);
  const [toast, setToast] = useState(null);
  // The Quick export summary opens on hover and pins on click.
  const [quickInfoPinned, setQuickInfoPinned] = useState(false);
  const [quickInfoHovered, setQuickInfoHovered] = useState(false);

  // 'loading' | 'synced' | 'saving' | 'offline'
  const [prefsState, setPrefsState] = useState('loading');
  const prefsReady = useRef(false);

  const abortRef = useRef(null);
  const columnMenuRef = useRef(null);
  const quickInfoRef = useRef(null);
  const [loadedRange, setLoadedRange] = useState(null);

  const showToast = (message, tone = 'info') => {
    setToast({ message, tone });
    setTimeout(() => setToast(null), 4000);
  };

  const runReport = useCallback(async (target) => {
    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    setIsLoading(true);
    setError(null);
    try {
      const result = await fetchVoidReport({ ...target, signal: controller.signal });
      setData(result);
      setLoadedRange(target);
      setPage(1);
    } catch (err) {
      if (err.name === 'AbortError') return;
      setError(err.message || 'The report could not be loaded.');
      setData(null);
    } finally {
      if (abortRef.current === controller) {
        setIsLoading(false);
        abortRef.current = null;
      }
    }
  }, []);

  const applyPrefs = useCallback((prefs) => {
    setRange(resolveRange(prefs.range));
    setVisibleColumns(prefs.tableColumns);
    setSort(prefs.sort);
    setPageSize(prefs.pageSize);
    setExportScope(prefs.exportScope);
    setExportPreset(prefs.export);
    setFilters({ search: '', ...prefs.filters });
  }, []);

  // Load this user's settings, then run the report for their saved range.
  useEffect(() => {
    const controller = new AbortController();
    let cancelled = false;

    fetchPreferences(controller.signal)
      .then(({ prefs, source }) => {
        if (cancelled) return;
        applyPrefs(prefs);
        setPrefsState('synced');
        // A preset carried over from the localStorage-only build: push it up so
        // it is theirs on every machine from now on.
        if (source === 'legacy') putPreferences(prefs).catch(() => {});
        prefsReady.current = true;
        runReport(resolveRange(prefs.range));
      })
      .catch((err) => {
        if (cancelled || err.name === 'AbortError') return;
        // The report still works; settings just will not follow them around.
        setPrefsState('offline');
        prefsReady.current = true;
        runReport(resolveRange(initial.range));
      });

    return () => {
      cancelled = true;
      controller.abort();
      if (abortRef.current) abortRef.current.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // The settings blob as it currently stands on screen.
  const currentPrefs = useMemo(() => ({
    ...defaultPrefs(),
    tableColumns: visibleColumns,
    sort,
    pageSize,
    range: rangeToPref(range),
    filters: {
      status: filters.status,
      voidedBy: filters.voidedBy,
      carrier: filters.carrier,
      reshipped: filters.reshipped,
    },
    exportScope,
    export: exportPreset,
  }), [visibleColumns, sort, pageSize, range, filters, exportScope, exportPreset]);

  // Auto-save, debounced. Gated on prefsReady so the first render never writes
  // defaults over what the server is about to hand back.
  useEffect(() => {
    if (!prefsReady.current) return undefined;
    const timer = setTimeout(() => {
      setPrefsState((s) => (s === 'offline' ? s : 'saving'));
      putPreferences(currentPrefs)
        .then(() => setPrefsState('synced'))
        .catch(() => setPrefsState('offline'));
    }, SAVE_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [currentPrefs]);

  useEffect(() => {
    if (!showColumnMenu) return undefined;
    const onClick = (e) => {
      if (columnMenuRef.current && !columnMenuRef.current.contains(e.target)) {
        setShowColumnMenu(false);
      }
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [showColumnMenu]);

  useEffect(() => {
    if (!quickInfoPinned) return undefined;
    const onClick = (e) => {
      if (quickInfoRef.current && !quickInfoRef.current.contains(e.target)) {
        setQuickInfoPinned(false);
      }
    };
    const onKey = (e) => { if (e.key === 'Escape') setQuickInfoPinned(false); };
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [quickInfoPinned]);

  const rows = useMemo(() => data?.rows || [], [data]);

  const filteredRows = useMemo(() => {
    const term = filters.search.trim().toLowerCase();
    return rows.filter((row) => {
      if (filters.status !== 'all' && row.status !== filters.status) return false;
      if (filters.voidedBy !== 'all' && (row.voidedBy || '(none)') !== filters.voidedBy) return false;
      if (filters.carrier !== 'all' && row.carrier !== filters.carrier) return false;
      if (filters.reshipped === 'yes' && row.reshipped !== true) return false;
      if (filters.reshipped === 'no' && row.reshipped !== false) return false;
      if (term) {
        const haystack = [
          row.orderNumber, row.masterTrackingNumber, row.replacementTracking,
          row.voidedBy, row.shipper,
        ].filter(Boolean).join(' ').toLowerCase();
        if (!haystack.includes(term)) return false;
      }
      return true;
    });
  }, [rows, filters]);

  const sortedRows = useMemo(() => {
    const col = COLUMN_BY_ID[sort.columnId];
    if (!col) return filteredRows;
    const dir = sort.direction === 'asc' ? 1 : -1;

    return [...filteredRows].sort((a, b) => {
      let va = a[sort.columnId];
      let vb = b[sort.columnId];

      // Nulls always sink to the bottom, whichever way the column is sorted.
      const aEmpty = va === null || va === undefined || va === '';
      const bEmpty = vb === null || vb === undefined || vb === '';
      if (aEmpty && bEmpty) return 0;
      if (aEmpty) return 1;
      if (bEmpty) return -1;

      if (col.type === 'number') return (Number(va) - Number(vb)) * dir;
      if (col.type === 'bool') return ((va === vb) ? 0 : (va ? 1 : -1)) * dir;
      // Datetimes are zero-padded ISO-like strings, so text order is time order.
      va = String(va); vb = String(vb);
      return va.localeCompare(vb, undefined, { numeric: true }) * dir;
    });
  }, [filteredRows, sort]);

  const filterSummary = useMemo(() => {
    const parts = [];
    if (filters.search) parts.push(`search "${filters.search}"`);
    if (filters.status !== 'all') parts.push(filters.status === 'V' ? 'voids only' : 'replacements only');
    if (filters.reshipped !== 'all') parts.push(filters.reshipped === 'yes' ? 'reshipped' : 'never reshipped');
    if (filters.voidedBy !== 'all') parts.push(`voided by ${filters.voidedBy}`);
    if (filters.carrier !== 'all') parts.push(filters.carrier);
    return parts.join(', ') || 'none';
  }, [filters]);

  const toggleTableColumn = (id) => {
    setVisibleColumns((current) => {
      const next = current.includes(id)
        ? current.filter((c) => c !== id)
        // Keep the table in registry order however the user ticks them.
        : COLUMNS.filter((c) => current.includes(c.id) || c.id === id).map((c) => c.id);
      return next.length ? next : current; // never leave a table with no columns
    });
  };

  // Exporting commits the configuration used, so Quick export repeats exactly
  // what was last downloaded.
  const handleExported = (csv, filename, count, preset, scope) => {
    downloadCsv(csv, filename);
    setExportPreset(preset);
    setExportScope(scope);
    setShowExport(false);
    showToast(`Exported ${count.toLocaleString()} row${count === 1 ? '' : 's'} to ${filename}`, 'success');
  };

  const quickExportRows = exportScope === 'all' ? rows : sortedRows;
  const quickExportFilename = buildFilename(
    exportPreset.options.filenameTemplate, loadedRange || range
  );
  const quickInfoVisible = quickInfoPinned || quickInfoHovered;

  const quickExport = () => {
    if (!quickExportRows.length) return;
    const csv = buildCsv(quickExportRows, exportPreset.selection, exportPreset.options);
    // The same value the (i) summary shows, so the panel cannot promise one
    // file name and the download deliver another.
    downloadCsv(csv, quickExportFilename);
    showToast(`Exported ${quickExportRows.length.toLocaleString()} rows to ${quickExportFilename}`, 'success');
  };

  const copyToClipboard = async () => {
    const tsv = buildCsv(sortedRows, exportPreset.selection, {
      ...exportPreset.options,
      delimiter: 'tab',
      includeBom: false,
      forceTextTracking: false,
    });
    try {
      await navigator.clipboard.writeText(tsv);
      showToast(`Copied ${sortedRows.length.toLocaleString()} rows — paste straight into a sheet`, 'success');
    } catch (_) {
      showToast('The browser blocked clipboard access.', 'error');
    }
  };

  const resetSettings = async () => {
    const fresh = defaultPrefs();
    prefsReady.current = false;      // stop the auto-save racing the reset
    applyPrefs(fresh);
    try {
      await deletePreferences();
      setPrefsState('synced');
      showToast('Your settings for this tile were reset to the defaults', 'success');
    } catch (_) {
      setPrefsState('offline');
      showToast('Reset locally, but the server copy could not be cleared.', 'error');
    } finally {
      prefsReady.current = true;
    }
  };

  const SettingsBadge = () => {
    const map = {
      loading: { icon: <Cloud className="w-3.5 h-3.5 animate-pulse" />, text: 'Loading your settings…', tone: 'text-slate-500' },
      saving:  { icon: <Cloud className="w-3.5 h-3.5 animate-pulse" />, text: 'Saving…', tone: 'text-slate-400' },
      synced:  { icon: <Cloud className="w-3.5 h-3.5" />, text: 'Settings saved to your account', tone: 'text-slate-500' },
      offline: { icon: <CloudOff className="w-3.5 h-3.5" />, text: 'Settings saved in this browser only', tone: 'text-amber-400' },
    };
    const s = map[prefsState] || map.synced;
    return (
      <span className={`flex items-center gap-1.5 text-xs ${s.tone}`} title={
        prefsState === 'offline'
          ? 'The settings service could not be reached, so changes are only kept in this browser.'
          : 'Columns, sort, page size, date range, filters and export settings follow your login.'
      }
      >
        {s.icon}
        <span>{s.text}</span>
      </span>
    );
  };

  return (
    // h-full + min-h-0 pins the tile to the height MainLayout gives it, so the
    // page itself never scrolls and the sidebar and header stay put. Only the
    // table body below scrolls.
    <div className="h-full min-h-0 flex flex-col gap-4">
      <div className="flex-shrink-0 flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold text-slate-100">Incomm VOID Report</h1>
          <p className="text-sm text-slate-400 mt-0.5">
            Voided shipping labels and their replacements, pulled live from ShipStore.
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <div className="relative" ref={columnMenuRef}>
            <button
              onClick={() => setShowColumnMenu((v) => !v)}
              className="px-3 py-2 rounded-md text-sm bg-slate-700 text-slate-200 hover:bg-slate-600 flex items-center gap-1.5"
            >
              <Columns className="w-4 h-4" />
              Columns
            </button>
            {showColumnMenu && (
              <div className="absolute right-0 mt-2 w-64 bg-slate-800 border border-slate-700 rounded-lg shadow-xl z-30 p-2">
                <p className="text-[11px] text-slate-500 px-2 pb-2">
                  Columns shown in the table below. Saved to your account.
                </p>
                <div className="max-h-80 overflow-y-auto">
                  {COLUMNS.map((col) => (
                    <button
                      key={col.id}
                      onClick={() => toggleTableColumn(col.id)}
                      className="w-full flex items-center gap-2 px-2 py-1.5 rounded text-sm text-slate-200 hover:bg-slate-700 text-left"
                    >
                      <span className={`w-4 h-4 rounded border flex items-center justify-center flex-shrink-0 ${
                        visibleColumns.includes(col.id) ? 'bg-blue-600 border-blue-600' : 'border-slate-500'
                      }`}
                      >
                        {visibleColumns.includes(col.id) && <Check className="w-3 h-3 text-white" />}
                      </span>
                      <span>{col.label}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          <button
            onClick={copyToClipboard}
            disabled={!sortedRows.length}
            className="px-3 py-2 rounded-md text-sm bg-slate-700 text-slate-200 hover:bg-slate-600
                       disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-1.5"
            title="Copy the filtered rows as tab-separated text"
          >
            <Copy className="w-4 h-4" />
            Copy
          </button>

          <div
            className="relative flex"
            ref={quickInfoRef}
            onMouseEnter={() => setQuickInfoHovered(true)}
            onMouseLeave={() => setQuickInfoHovered(false)}
          >
            <button
              onClick={quickExport}
              disabled={!quickExportRows.length}
              className="px-3 py-2 rounded-l-md text-sm bg-slate-700 text-slate-200 hover:bg-slate-600
                         disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-1.5"
              title="Repeat your saved export — same columns, order and formatting, no dialog"
            >
              <Download className="w-4 h-4" />
              Quick export
            </button>
            {/* A sibling button, not a nested one, so opening the summary can
                never fire the export. */}
            <button
              onClick={() => setQuickInfoPinned((v) => !v)}
              className="px-2 rounded-r-md bg-slate-700 text-slate-400 hover:bg-slate-600 hover:text-slate-200
                         border-l border-slate-600/70 flex items-center"
              aria-label="Show what Quick export will produce"
              aria-expanded={quickInfoVisible}
            >
              <Info className="w-3.5 h-3.5" />
            </button>

            {quickInfoVisible && (
              <div className="absolute right-0 top-full mt-2 z-40">
                <QuickExportSummary
                  preset={exportPreset}
                  scope={exportScope}
                  filteredCount={sortedRows.length}
                  allCount={rows.length}
                  filterSummary={filterSummary}
                  filename={quickExportFilename}
                />
              </div>
            )}
          </div>

          <button
            onClick={() => setShowExport(true)}
            disabled={!rows.length}
            className="px-4 py-2 rounded-md text-sm font-medium bg-green-600 text-white hover:bg-green-700
                       disabled:bg-slate-700 disabled:text-slate-500 disabled:cursor-not-allowed flex items-center gap-1.5"
          >
            <Download className="w-4 h-4" />
            Export CSV…
          </button>

          <button
            onClick={() => runReport(range)}
            disabled={isLoading}
            className="p-2 rounded-md bg-slate-700 text-slate-200 hover:bg-slate-600 disabled:opacity-40"
            title="Re-run the report"
            aria-label="Refresh"
          >
            <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      <DateRangePicker
        startDate={range.startDate}
        endDate={range.endDate}
        onChange={setRange}
        onRun={() => runReport(range)}
        isLoading={isLoading}
      />

      {error && (
        <div className="flex-shrink-0 bg-red-900/30 border border-red-800 text-red-300 px-4 py-3 rounded-lg flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="font-medium">The report could not be loaded.</p>
            <p className="text-sm text-red-400/90 mt-0.5">{error}</p>
          </div>
          <button
            onClick={() => runReport(range)}
            className="px-3 py-1.5 bg-red-800 hover:bg-red-700 rounded text-sm font-medium flex-shrink-0"
          >
            Try again
          </button>
        </div>
      )}

      {isLoading && !data && (
        <div className="flex-shrink-0 bg-slate-800 border border-slate-700 rounded-lg p-12 flex flex-col items-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mb-3" />
          <p className="text-slate-400 text-sm">Pulling the void report from ShipStore…</p>
        </div>
      )}

      {data && (
        <>
          <SummaryCards summary={data.summary} />

          {data.summary.rowsOutsideRequestedRange > 0 && (
            <div className="flex-shrink-0 bg-amber-900/20 border border-amber-800/60 text-amber-200 px-4 py-3 rounded-lg flex items-start gap-3 text-sm">
              <Info className="w-4 h-4 flex-shrink-0 mt-0.5" />
              <p>
                {data.summary.rowsOutsideRequestedRange} row
                {data.summary.rowsOutsideRequestedRange === 1 ? ' has a ship date' : 's have ship dates'} outside{' '}
                {loadedRange?.startDate} – {loadedRange?.endDate}. The upstream filter matches the void's ship date and
                returns the replacement whenever it shipped, so those rows belong to voids inside the range. They are
                flagged in the Ship Date column rather than dropped.
              </p>
            </div>
          )}

          <FilterBar
            filters={filters}
            onChange={(f) => { setFilters(f); setPage(1); }}
            onReset={() => { setFilters({ search: '', ...EMPTY_FILTERS }); setPage(1); }}
            voiders={data.summary.byVoider}
            carriers={data.summary.byCarrier}
            matchCount={filteredRows.length}
            totalCount={rows.length}
          />

          <VoidTable
            rows={sortedRows}
            isUnfiltered={rows.length === 0}
            visibleColumns={visibleColumns}
            sort={sort}
            onSortChange={(s) => { setSort(s); setPage(1); }}
            page={page}
            pageSize={pageSize}
            onPageChange={setPage}
            onPageSizeChange={(n) => { setPageSize(n); setPage(1); }}
          />

          <div className="flex-shrink-0 flex flex-wrap items-center justify-between gap-3 text-xs text-slate-500">
            <span className="flex items-center gap-2">
              <Clock className="w-3.5 h-3.5" />
              {loadedRange?.startDate} – {loadedRange?.endDate} · {rows.length.toLocaleString()} rows ·
              {' '}pulled {new Date(data.fetchedAt).toLocaleTimeString()} in {(data.upstreamMs / 1000).toFixed(1)}s
            </span>
            <span className="flex items-center gap-4">
              <SettingsBadge />
              <button
                onClick={resetSettings}
                className="flex items-center gap-1.5 text-slate-500 hover:text-slate-300"
                title="Clear your saved columns, filters, range and export settings for this tile"
              >
                <RotateCcw className="w-3.5 h-3.5" />
                Reset my settings
              </button>
            </span>
          </div>
        </>
      )}

      {showExport && (
        <ExportModal
          filteredRows={sortedRows}
          allRows={rows}
          startDate={(loadedRange || range).startDate}
          endDate={(loadedRange || range).endDate}
          filterSummary={filterSummary}
          initialSelection={exportPreset.selection}
          initialOptions={exportPreset.options}
          initialScope={exportScope}
          onPersist={(preset, scope) => { setExportPreset(preset); setExportScope(scope); }}
          onExported={handleExported}
          onClose={() => setShowExport(false)}
        />
      )}

      {toast && (
        <div
          className={`fixed bottom-6 right-6 px-4 py-3 rounded-lg shadow-lg text-sm z-[110] ${
            toast.tone === 'success' ? 'bg-green-700 text-white'
              : toast.tone === 'error' ? 'bg-red-700 text-white'
              : 'bg-slate-700 text-slate-100'
          }`}
        >
          {toast.message}
        </div>
      )}
    </div>
  );
};

export default IncommVoidReport;
